import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, RotateCw, Trash2, Eraser } from 'lucide-react';
import { api } from '@/api/client';
import type { Usage, UsagesResp } from '@/api/types';
import { cn } from '@/lib/utils';
import { parseCollectionAllocation } from '@/lib/resource-helpers';
import TablePagination from '@/components/resources/TablePagination';

export default function UsagesPage() {
  const { name = '' } = useParams<{ name: string }>();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(50);

  const { data, isLoading, isFetching, error, refetch } = useQuery({
    queryKey: ['usages', name],
    queryFn: () =>
      api<UsagesResp>(`/v1/usages?resource-id=${encodeURIComponent(name)}`),
    enabled: name !== '',
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });

  // Single mutation that deletes either one allocation (when allocationDate
  // is given) or every allocation for a cluster (when omitted). The v1 API
  // at internal/handler/v1/resource/usage.go branches on which params are
  // set. The `unix-time` query name is misleading: the handler feeds it to
  // db.ConvertToUnixNano which calls time.Parse(time.RFC3339Nano, ...). So
  // it expects the allocationDate string verbatim, not nanoseconds.
  const deleteMutation = useMutation({
    mutationFn: (vars: { clusterId: string; allocationDate?: string }) => {
      const params = new URLSearchParams();
      if (vars.allocationDate) {
        params.set('resource-id', name);
        params.set('cluster-id', vars.clusterId);
        params.set('unix-time', vars.allocationDate);
      } else {
        params.set('cluster-id', vars.clusterId);
      }
      return api<unknown>(`/v1/usages?${params.toString()}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['usages', name] });
      // The Resources list shows aggregate usage; keep it in sync.
      queryClient.invalidateQueries({ queryKey: ['resources'] });
    },
    onError: (err) =>
      alert(`Delete failed: ${err instanceof Error ? err.message : String(err)}`),
  });

  const rows = data?.usages ?? [];
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = useMemo(
    () => rows.slice(safePage * pageSize, (safePage + 1) * pageSize),
    [rows, safePage, pageSize],
  );

  useEffect(() => {
    setPage(0);
  }, [name]);

  return (
    <div className="space-y-4">
      <div className="space-y-1.5">
        <div className="flex items-center justify-between gap-3">
          <nav
            aria-label="Breadcrumb"
            className="flex items-center gap-1.5 text-sm text-muted-foreground min-w-0"
          >
            <Link
              to="/resources"
              className="hover:text-foreground transition-colors shrink-0"
            >
              Resources
            </Link>
            <ChevronRight
              className="h-3.5 w-3.5 shrink-0 opacity-60"
              aria-hidden
            />
            <span className="text-foreground truncate">Usages</span>
          </nav>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border hover:bg-muted disabled:opacity-50 transition-colors shrink-0"
          >
            <RotateCw className={cn('h-4 w-4', isFetching && 'animate-spin')} />
            Refresh
          </button>
        </div>
        <h1
          className="text-2xl font-semibold tracking-tight text-primary font-mono truncate"
          title={name}
        >
          {name}
        </h1>
      </div>

      {isLoading && (
        <div className="py-12 text-center text-muted-foreground text-sm">
          Loading current allocations…
        </div>
      )}

      {error && (
        <div className="py-3 px-4 rounded-md border border-destructive/30 bg-destructive/5 text-destructive text-sm">
          Failed to load usages:{' '}
          {error instanceof Error ? error.message : String(error)}
        </div>
      )}

      {!isLoading && !error && (
        <div className="border rounded-lg overflow-hidden bg-card">
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left">
                <th className="px-3 py-2.5 font-medium">Cluster</th>
                <th className="px-3 py-2.5 font-medium">User</th>
                <th className="px-3 py-2.5 font-medium">Project</th>
                <th className="px-3 py-2.5 font-medium">Department</th>
                <th className="px-3 py-2.5 font-medium">Amount</th>
                <th className="px-3 py-2.5 font-medium">Allocated</th>
                <th className="px-3 py-2.5 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && (
                <tr>
                  <td
                    colSpan={7}
                    className="py-16 text-center text-muted-foreground text-sm"
                  >
                    No current allocations.
                  </td>
                </tr>
              )}
              {pageRows.map((u, i) => (
                <UsageRow
                  key={`${u.allocationDate}-${u.clusterId}-${i}`}
                  usage={u}
                  resourceName={name}
                  onDeleteOne={() =>
                    deleteMutation.mutate({
                      clusterId: u.clusterId,
                      allocationDate: u.allocationDate,
                    })
                  }
                  onDeleteCluster={() =>
                    deleteMutation.mutate({ clusterId: u.clusterId })
                  }
                  busy={deleteMutation.isPending}
                />
              ))}
            </tbody>
          </table>
          <TablePagination
            page={safePage}
            pageSize={pageSize}
            totalRows={rows.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            rowLabel="allocation"
          />
        </div>
      )}
    </div>
  );
}

interface UsageRowProps {
  usage: Usage;
  resourceName: string;
  onDeleteOne: () => void;
  onDeleteCluster: () => void;
  busy: boolean;
}

function UsageRow({
  usage,
  resourceName,
  onDeleteOne,
  onDeleteCluster,
  busy,
}: UsageRowProps) {
  // Collection allocations carry which item indexes they hold inside
  // stringList["0"] (base64 JSON). Other types either bake their items into
  // `amount` already (StringList) or have none, so this is Collection-only.
  const collectionItems =
    usage.resourceType === 'Collection'
      ? parseCollectionAllocation(usage.stringList?.[0])
      : null;
  return (
    <tr className="border-t">
      <td className="px-3 py-1.5 font-mono text-xs break-all">
        {usage.clusterId || '—'}
      </td>
      <td className="px-3 py-1.5 font-medium">{usage.user || '—'}</td>
      <td className="px-3 py-1.5 text-muted-foreground">
        {usage.project || '—'}
      </td>
      <td className="px-3 py-1.5 text-muted-foreground">
        {usage.department || '—'}
      </td>
      <td className="px-3 py-1.5 font-mono">
        {usage.amount || '—'}
        {collectionItems && collectionItems.length > 0 && (
          <span
            className="ml-1 text-muted-foreground"
            title={collectionItems
              .map((it) => `[${it.index}] ${it.value}`)
              .join('\n')}
          >
            [{collectionItems.map((it) => it.index).join(', ')}]
          </span>
        )}
      </td>
      <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">
        {formatDate(usage.allocationDate)}
      </td>
      <td className="px-3 py-1.5 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Delete this allocation of ${usage.amount || '—'} ${resourceName} on cluster ${usage.clusterId}?`,
                )
              ) {
                onDeleteOne();
              }
            }}
            disabled={busy}
            className="inline-flex items-center justify-center p-1.5 rounded text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title="Delete this allocation"
            aria-label="Delete this allocation"
          >
            <Trash2 className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => {
              if (
                confirm(
                  `Delete ALL allocations for cluster ${usage.clusterId}? This affects every resource, not just ${resourceName}.`,
                )
              ) {
                onDeleteCluster();
              }
            }}
            disabled={busy}
            className="inline-flex items-center justify-center p-1.5 rounded text-destructive hover:bg-destructive/10 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            title={`Delete all allocations for cluster ${usage.clusterId}`}
            aria-label={`Delete all allocations for cluster ${usage.clusterId}`}
          >
            <Eraser className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function formatDate(s: string): string {
  if (!s) return '—';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

